import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  resetState,
  sendOfferEmail,
} from "../redux/Slices/sendOfferEmailSlice";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Toolbar,
  Tooltip,
  Paper,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatListNumbered,
  FormatListBulleted,
  AttachFile,
  Undo,
  Redo,
  InsertPhoto,
  InsertEmoticon,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Link,
  Send,
  Close,
  Delete,
  FormatColorText,
  FormatSize,
} from "@mui/icons-material";
import "../styles/_emailWriter.scss";
import useCustomTranslation from "../hooks/useCustomTranslation";
import ToastNotification from "./Common/ToastNotification";
import Loader from "./Common/Loader";
import { updateLeadStatusApi } from "../features/Lead/leadsAPI";
import config from "../config/config";
import appInsights from "../AppInsights";

const fontSizes = [
  { label: "Small", value: "2" },
  { label: "Medium", value: "3" },
  { label: "Large", value: "4" },
];

const fonts = [
  { label: "Sans Serif", value: "sans-serif" },
  { label: "Arial", value: "Arial" },
  { label: "Times New Roman", value: "Times New Roman" },
  { label: "Courier New", value: "Courier New" },
  { label: "Verdana", value: "Verdana" },
  { label: "Georgia", value: "Georgia" },
  { label: "Trebuchet MS", value: "Trebuchet MS" },
  { label: "Comic Sans MS", value: "Comic Sans MS" },
  { label: "Impact", value: "Impact" },
  { label: "Lucida Sans", value: "Lucida Sans" },
];

const emojis = [
  // Faces
  "😀",
  "😃",
  "😄",
  "😁",
  "😆",
  "😅",
  "🤣",
  "😂",
  "🙂",
  "🙃",
  "😉",
  "😊",
  "😇",
  "🥰",
  "😍",
  "🤩",
  "😘",
  "😗",
  "😚",
  "😙",
  "😋",
  "😛",
  "😜",
  "🤪",
  // Gestures
  "👍",
  "👎",
  "👏",
  "🙌",
  "🤝",
  "👊",
  "✊",
  "🤘",
  "👌",
  "✌️",
  "🤞",
  "🤟",
  "🙏",
  "✋",
  "🤚",
  "👋",
  "🖐️",
  "🤙",
  // Objects
  "🎉",
  "🎊",
  "💡",
  "🔔",
  "🎁",
  "📱",
  "💻",
  "⌚",
  "📷",
  "📚",
  "💼",
  "🖊️",
  "🖋️",
  "✏️",
  "🖌️",
  "🖍️",
  "📝",
  "📒",
  "📈",
  "📉",
  // Symbols
  "❤️",
  "💛",
  "💚",
  "💙",
  "💜",
  "🖤",
  "🤍",
  "🤎",
  "💔",
  "❣️",
  "💕",
  "💞",
  "💓",
  "💗",
  "💖",
  "💘",
  "💝",
  "💟",
  "🔅",
  "🔆",
  "✨",
];

const EmailWriter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const offerData = location.state || {};
  const { trans } = useCustomTranslation();

  const { profileData } = useSelector((state) => state.profile);
  const { message, isLoading, isSuccess, isError } = useSelector(
    (state) => state.leadOfferEmail,
  );
  const { leadOfferDetailsId, emailTo, coachName, leadName } = offerData;

  const editorRef = useRef(null);
  const lastSelectionRef = useRef(null);

  const [ccVisible, setCcVisible] = useState(false);
  const [bccVisible, setBccVisible] = useState(false);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [textColor, setTextColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [activeStyle, setActiveStyle] = useState({});

  const [emojiDrawerOpen, setEmojiDrawerOpen] = useState(false);
  const [selectedAlignment, setSelectedAlignment] = useState("");
  const [selectedList, setSelectedList] = useState("");
  const [to, setTo] = useState(emailTo || "");
  const [subject, setSubject] = useState(
    `Your Coaching Offer from ${coachName || "Your Coach"} – Get Started` || ""
  );
  const [toastConfig, setToastConfig] = useState(null);

  useEffect(() => {
    appInsights.trackPageView({
      name: "Email Writer Page",
      properties: {
        coachId: profileData?.coachId,
        leadId: offerData?.leadId,
        leadOfferDetailsId,
      },
    });
  }, [profileData?.coachId, offerData?.leadId, leadOfferDetailsId]);

  // const paymentLink = `https://coach-webapp-dev-fe-ehhtfmhxg8hadtg7.westus2-01.azurewebsites.net/#/confirmationPage/${leadOfferDetailsId}`;
  const paymentLink = `${config.MSAL_REDIRECT_URI}/confirmationPage/${leadOfferDetailsId}`;

  // Set initial content on mount
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = `
        <p>Hi ${leadName},</p>
        <p>So excited to get started, let’s do this! Just use the link below to get started and set up your account: </p>
        <p><a href=${
          paymentLink || "#"
        } style="border-radius: 5px; cursor:pointer; text-decoration:none"> 👉<span class="ms-2">Set up your account</span></a></p>
        <p>Thank you for your trust ${leadName} we are going to make some great progress.</p>
        <p>Best,<br/> ${coachName}</p>
      `;
    }
    // Add click handler for all links in the editor
    const handleEditorClick = (e) => {
      if (e.target.tagName === "A") {
        e.preventDefault();
        window.open(e.target.href, "_blank");
      }
    };

    editorRef.current.addEventListener("click", handleEditorClick);

    // Cleanup
    return () => {
      if (editorRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        editorRef.current.removeEventListener("click", handleEditorClick);
      }
    };
  }, [leadName, paymentLink, coachName]);

  // Save selection when editor loses focus or before updates
  const saveSelection = () => {
    const selection = window.getSelection();
    if (
      selection.rangeCount > 0 &&
      editorRef.current.contains(selection.anchorNode)
    ) {
      lastSelectionRef.current = selection.getRangeAt(0);
    }
  };

  // Restore selection after updates
  const restoreSelection = () => {
    if (lastSelectionRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(lastSelectionRef.current);
    }
  };

  // Handle input events
  const handleInput = () => {
    saveSelection();
    // Restore selection after state update
    setTimeout(restoreSelection, 0);
  };

  const handleInsertEmoji = (emoji) => {
    handleExecCommand("insertText", emoji);
    setEmojiDrawerOpen(false);
  };

  const handleFontSizeChange = (event) => {
    const size = event.target.value;
    handleExecCommand("fontSize", size);
  };

  const handleFontChange = (event) => {
    const font = event.target.value;
    handleExecCommand("fontName", font);
  };

  const handleExecCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
  };

  const handleToolbarClick = (command, value = null) => {
    handleExecCommand(command, value);
    setActiveStyle((prev) => ({
      ...prev,
      [command]: prev[command] ? false : true,
    }));
  };

  const handleAddLink = () => {
    if (linkValue) {
      handleExecCommand("createLink", linkValue);
    }
    setLinkDialogOpen(false);
    setLinkValue("");
  };

  const handleApplyColors = () => {
    handleExecCommand("foreColor", textColor);
    handleExecCommand("hiliteColor", bgColor);
    setColorPickerVisible(false);
    editorRef.current.focus();
  };

  const handleList = (listType) => {
    handleExecCommand(
      listType === "numbered" ? "insertOrderedList" : "insertUnorderedList"
    );
    editorRef.current.focus();
  };

  const handleUndoRedo = (action) => {
    handleExecCommand(action);
  };

  const handleInsertPicture = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        handleExecCommand("insertImage", e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSend = async () => {
    appInsights.trackEvent({
      name: "Email_writer_page: Send Email Clicked",
      properties: {
        coachId: profileData?.coachId,
        leadId: offerData?.leadId,
      },
    });

    const emailContent = editorRef.current.innerHTML;
    const plainTextBody = editorRef.current.textContent;

    const payload = {
      toEmail: to,
      emailSubject: subject,
      content: emailContent.trim(),
      leadId: offerData?.leadId,
      coachId: profileData?.coachId,
      plainTextBody: plainTextBody.trim(),
      leadOfferDetailsId: leadOfferDetailsId,
    };
    dispatch(sendOfferEmail(payload));
  };

  useEffect(() => {
    const updateLeadStatus = async () => {
      try {
        await updateLeadStatusApi(
          profileData?.coachId,
          offerData?.leadId,
          "Offer sent",
        );
      } catch (error) {
        console.error("Failed to update lead status:", error);
      }
    };
    if (isSuccess) {
      // Trigger success toast
      setToastConfig({
        type: "success",
        message: message,
        heading: trans("Common.success"),
      });
      updateLeadStatus();
      setTimeout(() => {
        dispatch(resetState());
        navigate("/leads");
      }, 3000);
    } else if (isError) {
      appInsights.trackException({
        exception: new Error(message),
        severityLevel: 2,
        properties: {
          context: "Email_writer_page: Offer Email Send Failed",
        },
      });
      // Trigger success toast
      setToastConfig({
        type: "error",
        message: message,
        heading: trans("Common.failed"),
      });
      setTimeout(() => {
        dispatch(resetState());
        navigate("/leads");
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isSuccess, isError, message, trans]);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {/* Header Section (Same as Add Offer Detail) */}
          <div className="section-container header-section d-flex justify-content-between">
            {/* Left Side: Back Button & Step Labels */}
            <div className="d-flex align-items-center">
              <Button
                variant="outline-primary"
                className="cancel-button"
                onClick={() => navigate("/leads")}
              >
                <i className="bi bi-chevron-left"></i> Back
              </Button>

              {/* Step Labels */}
              <div className="mt-2 d-flex">
                <h5 className="ms-3 fw-bold text-muted">Add Offer Details</h5>
                <h5 className="ms-2 fw-bold">{">"}</h5>
                <h5 className="ms-2 fw-bold text-dark">Verify Email</h5>
              </div>
            </div>

            {/* Right Side: Send Email Button */}
            {/* <div>
              <Button className="send-email-button">
                <i className="bi bi-envelope-fill"></i> Send Email
              </Button>
            </div> */}
          </div>

          <Paper className="email-writer">
            <Toolbar className="toolbar">
              <Typography variant="h6">New Message</Typography>
              <Tooltip title="Close">
                <IconButton>
                  <Close />
                </IconButton>
              </Tooltip>
            </Toolbar>
            <Box className="content">
              <Box className="form-group">
                <Typography className="label">To</Typography>
                <TextField
                  disabled={true}
                  className="input"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
                <Box className="cc-bcc-buttons">
                  <Button onClick={() => setCcVisible(!ccVisible)}>CC</Button>
                  <Button onClick={() => setBccVisible(!bccVisible)}>
                    BCC
                  </Button>
                </Box>
              </Box>
              {ccVisible && (
                <Box className="form-group">
                  <Typography className="label">CC</Typography>
                  <TextField
                    className="input"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                  />
                </Box>
              )}
              {bccVisible && (
                <Box className="form-group">
                  <Typography className="label">BCC</Typography>
                  <TextField
                    className="input"
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                  />
                </Box>
              )}
              <Box className="form-group">
                <Typography className="label">Subject</Typography>
                <TextField
                  className="input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </Box>
              <Box
                className="editor"
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onBlur={saveSelection}
              ></Box>

              <Toolbar className="toolbar-options">
                {/* Font Selection */}
                <Select
                  className="select"
                  defaultValue="sans-serif"
                  displayEmpty
                  onChange={handleFontChange}
                >
                  <MenuItem value="" disabled>
                    Select Font
                  </MenuItem>
                  {fonts.map((font) => (
                    <MenuItem key={font.value} value={font.value}>
                      {font.label}
                    </MenuItem>
                  ))}
                </Select>

                {/* Font Size */}
                <Select
                  className="select"
                  defaultValue=""
                  displayEmpty
                  onChange={handleFontSizeChange}
                >
                  <MenuItem value="" disabled>
                    <Box>
                      <FormatSize />
                    </Box>
                  </MenuItem>
                  {fontSizes.map((size) => (
                    <MenuItem key={size.value} value={size.value}>
                      {size.label}
                    </MenuItem>
                  ))}
                </Select>

                <Tooltip title="Undo">
                  <IconButton onClick={() => handleUndoRedo("undo")}>
                    <Undo />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Redo">
                  <IconButton onClick={() => handleUndoRedo("redo")}>
                    <Redo />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Bold">
                  <IconButton
                    onClick={() => handleToolbarClick("bold")}
                    className={activeStyle.bold ? "active" : ""}
                  >
                    <FormatBold />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Italic">
                  <IconButton
                    onClick={() => handleToolbarClick("italic")}
                    className={activeStyle.italic ? "active" : ""}
                  >
                    <FormatItalic />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Underline">
                  <IconButton
                    onClick={() => handleToolbarClick("underline")}
                    className={activeStyle.underline ? "active" : ""}
                  >
                    <FormatUnderlined />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Strikethrough">
                  <IconButton
                    onClick={() => handleToolbarClick("strikeThrough")}
                    className={activeStyle.strikeThrough ? "active" : ""}
                  >
                    <FormatStrikethrough />
                  </IconButton>
                </Tooltip>

                {/* Alignment */}
                <Select
                  className="select"
                  value={selectedAlignment}
                  displayEmpty
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedAlignment(value);
                    handleExecCommand(value);
                  }}
                  renderValue={(selected) => {
                    if (!selected) return <FormatAlignLeft />;
                    if (selected === "justifyLeft") return <FormatAlignLeft />;
                    if (selected === "justifyCenter")
                      return <FormatAlignCenter />;
                    if (selected === "justifyRight")
                      return <FormatAlignRight />;
                  }}
                >
                  <MenuItem value="justifyLeft">
                    <Box>
                      <FormatAlignLeft />
                      Left Align
                    </Box>
                  </MenuItem>
                  <MenuItem value="justifyCenter">
                    <Box>
                      <FormatAlignCenter />
                      Center Align
                    </Box>
                  </MenuItem>
                  <MenuItem value="justifyRight">
                    <Box>
                      <FormatAlignRight />
                      Right Align
                    </Box>
                  </MenuItem>
                </Select>

                {/* Colors */}
                <Tooltip title="Text Color">
                  <IconButton onClick={() => setColorPickerVisible(true)}>
                    <FormatColorText />
                  </IconButton>
                </Tooltip>

                {/* Other Features */}
                <Tooltip title="Insert Link">
                  <IconButton onClick={() => setLinkDialogOpen(true)}>
                    <Link />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Insert Picture">
                  <IconButton component="label">
                    <InsertPhoto />
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleInsertPicture}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Insert Emoji">
                  <IconButton onClick={() => setEmojiDrawerOpen(true)}>
                    <InsertEmoticon />
                  </IconButton>
                </Tooltip>

                <Select
                  className="select"
                  value={selectedList}
                  displayEmpty
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedList(value);
                    if (value === "none") {
                      handleExecCommand("insertParagraph");
                    } else {
                      handleList(value);
                    }
                  }}
                  renderValue={(selected) => {
                    if (!selected) return <FormatListBulleted />;
                    if (selected === "numbered") return <FormatListNumbered />;
                    if (selected === "unordered") return <FormatListBulleted />;
                    if (selected === "none") return <Delete />;
                  }}
                >
                  <MenuItem value="numbered">
                    <Box>
                      <FormatListNumbered />
                      Numbered List
                    </Box>
                  </MenuItem>
                  <MenuItem value="unordered">
                    <Box>
                      <FormatListBulleted />
                      Unordered List
                    </Box>
                  </MenuItem>
                  <MenuItem value="none">
                    <Box>
                      <Delete />
                      Clear List
                    </Box>
                  </MenuItem>
                </Select>

                <Tooltip title="Attach File">
                  <IconButton component="label">
                    <AttachFile />
                    <input type="file" hidden />
                  </IconButton>
                </Tooltip>
              </Toolbar>

              <Box className="footer-buttons">
                <Button
                  className="create-button"
                  variant="contained"
                  startIcon={<Send />}
                  onClick={handleSend}
                >
                  Send
                </Button>
                <IconButton className="delete-button">
                  <Delete />
                </IconButton>
              </Box>

              <Dialog
                open={linkDialogOpen}
                onClose={() => setLinkDialogOpen(false)}
              >
                <DialogTitle>Insert Link</DialogTitle>
                <DialogContent>
                  <TextField
                    fullWidth
                    label="URL"
                    value={linkValue}
                    onChange={(e) => setLinkValue(e.target.value)}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setLinkDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddLink}>Insert</Button>
                </DialogActions>
              </Dialog>

              <Dialog
                open={colorPickerVisible}
                onClose={() => setColorPickerVisible(false)}
              >
                <DialogTitle>Pick Colors</DialogTitle>
                <DialogContent>
                  <Box className="color-picker">
                    <Typography>Text Color</Typography>
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                    />
                    <Typography>Background Color</Typography>
                    <input
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setColorPickerVisible(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleApplyColors}>Apply</Button>
                </DialogActions>
              </Dialog>

              <Dialog
                open={emojiDrawerOpen}
                onClose={() => setEmojiDrawerOpen(false)}
              >
                <DialogTitle>Pick an Emoji</DialogTitle>
                <DialogContent>
                  <Box className="emoji-picker">
                    {emojis.map((emoji) => (
                      <Button
                        key={emoji}
                        onClick={() => handleInsertEmoji(emoji)}
                      >
                        {emoji}
                      </Button>
                    ))}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setEmojiDrawerOpen(false)}>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Paper>

          {toastConfig && (
            <ToastNotification
              type={toastConfig.type}
              message={toastConfig.message}
              heading={toastConfig.heading}
            />
          )}
        </>
      )}
    </>
  );
};

export default EmailWriter;
